import { useSelector } from 'react-redux';
import { Box, Flex, useClipboard } from '@chakra-ui/react';
import { Field } from 'formik';
import { MdContentCopy } from 'react-icons/md';
import { Button, Text } from '@/components/v4';
import NameField from '@/components/forms/new/formikFormFields/NameField';
import TrunkFieldRow from './TrunkFieldRow';

/**
 * A freshly generated password, shown once.
 *
 * The value is never returned by the API again, so it is called out rather than
 * dropped into the masked input it replaces.
 */
const NewPasswordCallout = ({ password, darkMode }) => {
    const { onCopy, hasCopied } = useClipboard(password);

    return (
        <Box
            mt="15px"
            p={3}
            borderWidth="1px"
            borderRadius="md"
            borderColor="orange.300"
            bg={darkMode ? 'rgba(251, 211, 141, 0.16)' : 'orange.50'}>
            <Flex align="center" gap={3}>
                <Text
                    fontFamily="mono"
                    fontWeight="bold"
                    color={darkMode ? 'white' : 'gray.900'}
                    sx={{ wordBreak: 'break-all' }}>
                    {password}
                </Text>
                <Button
                    size="xs"
                    variant="outline"
                    flexShrink={0}
                    leftIcon={<MdContentCopy />}
                    onClick={onCopy}>
                    {hasCopied ? 'Copied' : 'Copy'}
                </Button>
            </Flex>
            <Text
                fontSize="xs"
                mt={2}
                color={darkMode ? 'orange.100' : 'orange.800'}>
                New password — copy it now. Navigating away from this page will
                clear it from the screen, and any devices using this trunk must
                be reconfigured.
            </Text>
        </Box>
    );
};

/**
 * SIP registration details for an existing SRT trunk.
 *
 * Credentials only exist once the trunk has been created, so this renders in
 * edit mode only. The password is masked until a regenerate returns a fresh
 * one, and that value is lost on navigation.
 */
const SRTSipRegistrationSection = ({
    authPassword,
    isRegenerating,
    onRegenerate,
}) => {
    const { darkMode } = useSelector((state) => state.settings);

    return (
        <>
            <TrunkFieldRow
                label="User"
                tooltip="SIP username for this trunk's registration"
                ariaLabel="user-tip">
                <Field
                    name="sipUsername"
                    component={NameField}
                    darkMode={darkMode}
                    isReadOnly
                    copyable
                    fontFamily="mono"
                />
            </TrunkFieldRow>

            <TrunkFieldRow
                label="Password"
                tooltip="SIP password — use Regenerate Password to update it"
                ariaLabel="password-tip"
                isLast>
                {authPassword ? (
                    <NewPasswordCallout
                        password={authPassword}
                        darkMode={darkMode}
                    />
                ) : (
                    <Field
                        name="password"
                        component={NameField}
                        darkMode={darkMode}
                        isReadOnly
                        type="password"
                        fontFamily="mono"
                    />
                )}
                <Button
                    mt={3}
                    size="sm"
                    isLoading={isRegenerating}
                    onClick={onRegenerate}>
                    Regenerate Password
                </Button>
            </TrunkFieldRow>
        </>
    );
};

export default SRTSipRegistrationSection;
